import React, { useState, useEffect, useRef } from "react";
import Swal from "sweetalert2";
import { useTranslation } from "react-i18next";
import {
  getAllLogs,
  exportLogsAsJSON,
  exportLogsAsCSV,
  clearLogs,
  getLogsSummary,
} from "../../utils/checkoutTracker";

// Allowed IP addresses
const ALLOWED_IPS = ["195.174.126.93"];

/**
 * Checkout Logs Viewer - View checkout tracking logs for the admin panel
 * IP-based access control is applied
 */
const CheckoutLogs = () => {
  const { t } = useTranslation();
  const [logs, setLogs] = useState([]);
  const [summary, setSummary] = useState(null);
  const [selectedLog, setSelectedLog] = useState(null);
  const [filter, setFilter] = useState("all");
  const [isAuthorized, setIsAuthorized] = useState(false);
  const [isChecking, setIsChecking] = useState(true);
  const hasCheckedIP = useRef(false); // Check IP only once

  // IP check
  useEffect(() => {
    // If already checked, don't check again
    if (hasCheckedIP.current) return;
    hasCheckedIP.current = true;

    const checkIPAccess = async () => {
      try {
        // Get user's IP address
        const response = await fetch("https://api.ipify.org?format=json");
        const data = await response.json();
        const userIP = data.ip;

        if (ALLOWED_IPS.includes(userIP)) {
          setIsAuthorized(true);
          loadLogs();
        } else {
          setIsAuthorized(false);
          Swal.fire({
            icon: "error",
            title: t("CheckoutLogs.title"),
            text: t("CheckoutLogs.text"),
            showConfirmButton: false,
            allowOutsideClick: false,
            allowEscapeKey: false,
            timer: 2000,
            timerProgressBar: true,
          }).then(() => {
            window.location.href = "/";
          });
        }
      } catch (error) {
        console.error("IP check failed:", error);
        setIsAuthorized(false);
        Swal.fire({
          icon: "error",
          title: t("CheckoutLogs.errorTitle"),
          text: t("CheckoutLogs.errorText"),
          showConfirmButton: false,
          allowOutsideClick: false,
          allowEscapeKey: false,
          timer: 2000,
          timerProgressBar: true,
        }).then(() => {
          window.location.href = "/";
        });
      } finally {
        setIsChecking(false);
      }
    };

    checkIPAccess();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadLogs = () => {
    const allLogs = getAllLogs();
    setLogs(allLogs.reverse()); // Newest first
    setSummary(getLogsSummary());
  };

  const handleClearLogs = () => {
    if (window.confirm("Are you sure you want to delete all logs?")) {
      clearLogs();
      loadLogs();
    }
  };

  const filteredLogs = logs.filter((log) => {
    if (filter === "all") return true;
    return log.status === filter;
  });

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleString("tr-TR");
  };

  const getStatusBadge = (status) => {
    const badges = {
      completed: "bg-success",
      abandoned: "bg-warning text-dark",
      failed: "bg-danger",
      in_progress: "bg-info",
    };
    return badges[status] || "bg-secondary";
  };

  // Authorization check - don't render content while loading or unauthorized
  if (isChecking) {
    return (
      <div className="container-fluid py-5 text-center">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <p className="mt-3 text-muted">Checking access...</p>
      </div>
    );
  }

  if (!isAuthorized) {
    return null; // SweetAlert will show and redirect
  }

  return (
    <div className="container py-4">
      <div className="row mb-4">
        <div className="col-12 text-center my-5">
          <h2>Checkout Tracking Logs</h2>
          <p className="text-muted">User checkout behavior and error records</p>
        </div>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="row mb-4">
          <div className="col-md-3">
            <div className="card bg-primary text-white">
              <div className="card-body">
                <h5 className="card-title">Total Sessions</h5>
                <h2>{summary.totalSessions}</h2>
              </div>
            </div>
          </div>
          <div className="col-md-3">
            <div className="card bg-success text-white">
              <div className="card-body">
                <h5 className="card-title">Completed</h5>
                <h2>{summary.completed}</h2>
              </div>
            </div>
          </div>
          <div className="col-md-3">
            <div className="card bg-warning">
              <div className="card-body">
                <h5 className="card-title">Abandoned</h5>
                <h2>{summary.abandoned}</h2>
              </div>
            </div>
          </div>
          <div className="col-md-3">
            <div className="card bg-danger text-white">
              <div className="card-body">
                <h5 className="card-title">Failed</h5>
                <h2>{summary.failed}</h2>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Statistics */}
      {summary && (
        <div className="row mb-4">
          <div className="col-md-6">
            <div className="card">
              <div className="card-body">
                <h5 className="card-title">Average Form Completion</h5>
                <div className="progress" style={{ height: "25px" }}>
                  <div
                    className="progress-bar bg-info"
                    style={{ width: `${summary.averageFormCompletion}%` }}
                  >
                    {summary.averageFormCompletion}%
                  </div>
                </div>
                <small className="text-muted mt-2 d-block">
                  Average session duration:{" "}
                  {Math.floor(summary.averageDuration / 60)} min{" "}
                  {summary.averageDuration % 60} sec
                </small>
              </div>
            </div>
          </div>
          <div className="col-md-6">
            <div className="card">
              <div className="card-body">
                <h5 className="card-title">Abandon Reasons</h5>
                {Object.keys(summary.abandonReasons).length > 0 ? (
                  <ul className="list-unstyled mb-0">
                    {Object.entries(summary.abandonReasons).map(
                      ([reason, count]) => (
                        <li key={reason}>
                          <strong>{reason}:</strong> {count}
                        </li>
                      )
                    )}
                  </ul>
                ) : (
                  <p className="text-muted mb-0">No abandonment records yet</p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="row mb-4">
        <div className="col-12">
          <div className="btn-group me-3">
            <button
              className={`btn ${
                filter === "all" ? "btn-primary" : "btn-outline-primary"
              }`}
              onClick={() => setFilter("all")}
            >
              All
            </button>
            <button
              className={`btn ${
                filter === "completed" ? "btn-success" : "btn-outline-success"
              }`}
              onClick={() => setFilter("completed")}
            >
              Completed
            </button>
            <button
              className={`btn ${
                filter === "abandoned" ? "btn-warning" : "btn-outline-warning"
              }`}
              onClick={() => setFilter("abandoned")}
            >
              Abandoned
            </button>
            <button
              className={`btn ${
                filter === "failed" ? "btn-danger" : "btn-outline-danger"
              }`}
              onClick={() => setFilter("failed")}
            >
              Failed
            </button>
          </div>

          <button
            className="btn btn-outline-primary me-2"
            onClick={() => exportLogsAsJSON()}
          >
            <i className="fas fa-download me-1"></i> Export JSON
          </button>
          <button
            className="btn btn-outline-success me-2"
            onClick={() => exportLogsAsCSV()}
          >
            <i className="fas fa-file-csv me-1"></i> Export CSV
          </button>
          <button className="btn btn-outline-secondary me-2" onClick={loadLogs}>
            <i className="fas fa-refresh me-1"></i> Refresh
          </button>
          <button className="btn btn-outline-danger" onClick={handleClearLogs}>
            <i className="fas fa-trash me-1"></i> Clear
          </button>
        </div>
      </div>

      {/* Logs Table */}
      <div className="row mb-5">
        <div className={selectedLog ? "col-md-8" : "col-12"}>
          <div className="card">
            <div className="card-header">
              <h5 className="mb-0">
                Checkout Sessions ({filteredLogs.length})
              </h5>
            </div>
            <div className="card-body p-0">
              <div className="table-responsive">
                <table className="table table-hover mb-0">
                  <thead className="table-light">
                    <tr>
                      <th>Date</th>
                      <th>User</th>
                      <th>Status</th>
                      <th>Form %</th>
                      <th>Duration</th>
                      <th>Cart</th>
                      <th>Payment Attempts</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredLogs.map((log) => (
                      <tr
                        key={log.sessionId}
                        onClick={() => setSelectedLog(log)}
                        style={{ cursor: "pointer" }}
                        className={
                          selectedLog?.sessionId === log.sessionId
                            ? "table-active"
                            : ""
                        }
                      >
                        <td>{formatDate(log.startTime)}</td>
                        <td>
                          {log.userId === "guest" ? (
                            <span className="badge bg-secondary">Guest</span>
                          ) : (
                            <span className="badge bg-primary">
                              #{log.userId}
                            </span>
                          )}
                        </td>
                        <td>
                          <span
                            className={`badge ${getStatusBadge(log.status)}`}
                          >
                            {log.status}
                          </span>
                        </td>
                        <td>
                          <div
                            className="progress"
                            style={{ width: "60px", height: "8px" }}
                          >
                            <div
                              className="progress-bar"
                              style={{
                                width: `${log.formCompletionPercentage || 0}%`,
                              }}
                            ></div>
                          </div>
                          <small>{log.formCompletionPercentage || 0}%</small>
                        </td>
                        <td>
                          {log.durationSeconds
                            ? `${Math.floor(log.durationSeconds / 60)}:${String(
                                log.durationSeconds % 60
                              ).padStart(2, "0")}`
                            : "-"}
                        </td>
                        <td>{log.cartItems?.length || 0} items</td>
                        <td>
                          {log.paymentAttempts?.length || 0}
                          {log.paymentAttempts?.some(
                            (a) => a.status === "failed"
                          ) && <span className="text-danger ms-1">❌</span>}
                        </td>
                        <td>
                          <button
                            className="btn btn-sm btn-outline-primary"
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedLog(log);
                            }}
                          >
                            Details
                          </button>
                        </td>
                      </tr>
                    ))}
                    {filteredLogs.length === 0 && (
                      <tr>
                        <td colSpan="8" className="text-center py-4 text-muted">
                          No logs found yet
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>

        {/* Detail Panel */}
        {selectedLog && (
          <div className="col-md-4">
            <div className="card sticky-top" style={{ top: "20px" }}>
              <div className="card-header d-flex justify-content-between align-items-center">
                <h5 className="mb-0">Session Details</h5>
                <button
                  className="btn btn-sm btn-outline-secondary"
                  onClick={() => setSelectedLog(null)}
                >
                  ✕
                </button>
              </div>
              <div
                className="card-body"
                style={{ maxHeight: "70vh", overflowY: "auto" }}
              >
                <h6>General Information</h6>
                <table className="table table-sm">
                  <tbody>
                    <tr>
                      <td>Session ID</td>
                      <td>
                        <small>{selectedLog.sessionId}</small>
                      </td>
                    </tr>
                    <tr>
                      <td>Start</td>
                      <td>{formatDate(selectedLog.startTime)}</td>
                    </tr>
                    <tr>
                      <td>End</td>
                      <td>{formatDate(selectedLog.endTime)}</td>
                    </tr>
                    <tr>
                      <td>Currency</td>
                      <td>{selectedLog.currency || "-"}</td>
                    </tr>
                    <tr>
                      <td>Abandon Reason</td>
                      <td>{selectedLog.abandonReason || "-"}</td>
                    </tr>
                  </tbody>
                </table>

                <h6 className="mt-3">Form Fields (Entered Values)</h6>
                <table className="table table-sm">
                  <thead>
                    <tr>
                      <th>Field</th>
                      <th>Status</th>
                      <th>Value</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(selectedLog.formFields || {}).map(
                      ([field, data]) => (
                        <tr key={field}>
                          <td>
                            <small>{field}</small>
                          </td>
                          <td>
                            {data.filled ? (
                              <span className="text-success">✓</span>
                            ) : (
                              <span className="text-danger">✗</span>
                            )}
                          </td>
                          <td>
                            <small className="text-break">
                              {data.value || (
                                <span className="text-muted">-</span>
                              )}
                            </small>
                          </td>
                        </tr>
                      )
                    )}
                  </tbody>
                </table>

                {selectedLog.paymentAttempts?.length > 0 && (
                  <>
                    <h6 className="mt-3">Payment Attempts</h6>
                    {selectedLog.paymentAttempts.map((attempt, i) => (
                      <div
                        key={i}
                        className={`alert ${
                          attempt.status === "success"
                            ? "alert-success"
                            : "alert-danger"
                        } py-2`}
                      >
                        <strong>#{attempt.attemptNumber}</strong> -{" "}
                        {attempt.paymentMethod}
                        <br />
                        <small>Status: {attempt.status}</small>
                        {attempt.errorCode && (
                          <>
                            <br />
                            <small>
                              Error: {attempt.errorCode} -{" "}
                              {attempt.errorMessage}
                            </small>
                          </>
                        )}
                      </div>
                    ))}
                  </>
                )}

                {selectedLog.validationErrors?.length > 0 && (
                  <>
                    <h6 className="mt-3">Validation Errors</h6>
                    {selectedLog.validationErrors.map((error, i) => (
                      <div key={i} className="alert alert-warning py-2">
                        <strong>{error.field}</strong>: {error.errorType}
                        <br />
                        <small>{error.errorMessage}</small>
                      </div>
                    ))}
                  </>
                )}

                {selectedLog.cartItems?.length > 0 && (
                  <>
                    <h6 className="mt-3">Cart Contents</h6>
                    <ul className="list-group list-group-flush">
                      {selectedLog.cartItems.map((item, i) => (
                        <li key={i} className="list-group-item px-0 py-1">
                          <small>
                            {item.name} x{item.quantity}
                          </small>
                        </li>
                      ))}
                    </ul>
                  </>
                )}

                <h6 className="mt-3">Events Timeline</h6>
                <div className="timeline">
                  {selectedLog.events?.slice(-10).map((event, i) => (
                    <div key={i} className="mb-2">
                      <small className="text-muted">
                        {formatDate(event.timestamp)}
                      </small>
                      <br />
                      <span className="badge bg-secondary">{event.type}</span>
                      {event.data && Object.keys(event.data).length > 0 && (
                        <small className="d-block text-muted">
                          {JSON.stringify(event.data)}
                        </small>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CheckoutLogs;
